import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { CustomOrderDialog } from "./CustomOrderDialog";

const LOGO_URL = "/logo.png";

const navLinks = [
  { label: "Inicio", target: "#inicio", id: "inicio", type: "anchor" },
  { label: "Nosotros", target: "#nosotros", id: "nosotros", type: "anchor" },
  { label: "Categorías", target: "/categorias", id: "categorias", type: "route" },
  { label: "Personalizados", id: "personalizados", type: "modal" },
];

export const Navbar = () => {
  const { count, openCart } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleNavClick = (e, link) => {
    if (link.type === "modal") {
      e.preventDefault();
      setMobileOpen(false);
      setCustomOpen(true);
    } else if (link.type === "route") {
      e.preventDefault();
      setMobileOpen(false);
      navigate(link.target);
    } else {
      handleAnchor(e, link.target);
    }
  };

  const isActiveRoute = (link) =>
    link.type === "route" && location.pathname.startsWith(link.target);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[#E85A3C]/95 backdrop-blur-md border-b border-[#D3492D] shadow-md shadow-[#E85A3C]/20"
            : "bg-[#E85A3C] border-b border-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            data-testid="navbar-logo"
            className="flex items-center gap-3 group"
          >
            <span className="block h-10 w-10 md:h-11 md:w-11 rounded-full bg-white overflow-hidden ring-2 ring-white/40 transition-transform duration-300 group-hover:scale-105">
              <img
                src={LOGO_URL}
                alt="acuadros.co logo"
                className="h-full w-full object-cover"
              />
            </span>
            <span className="font-serif text-xl md:text-2xl tracking-tight text-white">
              acuadros<span className="text-[#C8D5C0]">.co</span>
            </span>
          </Link>

          {/* Desktop links */}
          <ul className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <li key={link.id}>
                <a
                  href={link.target || "#"}
                  onClick={(e) => handleNavClick(e, link)}
                  data-testid={`nav-link-${link.id}`}
                  className={`relative text-sm font-medium transition-colors duration-300 after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:bg-white after:transition-all after:duration-300 hover:after:w-full ${
                    isActiveRoute(link)
                      ? "text-white after:w-full"
                      : "text-white/85 hover:text-white after:w-0"
                  }`}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>

          {/* Cart + mobile toggle */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openCart}
              data-testid="navbar-cart-button"
              aria-label="Carrito de compras"
              className="relative p-2.5 rounded-full text-white hover:bg-white/15 transition-all duration-300"
            >
              <ShoppingBag className="h-5 w-5" strokeWidth={1.75} />
              {count > 0 && (
                <span
                  data-testid="cart-badge"
                  className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 rounded-full bg-white text-[#E85A3C] text-[11px] font-bold flex items-center justify-center shadow-md"
                >
                  {count}
                </span>
              )}
            </button>

            <button
              type="button"
              data-testid="navbar-mobile-toggle"
              aria-label="Abrir menú"
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden p-2.5 rounded-full text-white hover:bg-white/15 transition-all duration-300"
            >
              {mobileOpen ? (
                <X className="h-5 w-5" strokeWidth={1.75} />
              ) : (
                <Menu className="h-5 w-5" strokeWidth={1.75} />
              )}
            </button>
          </div>
        </nav>

        {/* Mobile menu */}
        <div
          data-testid="navbar-mobile-menu"
          className={`md:hidden overflow-hidden transition-all duration-300 bg-[#E85A3C]/97 backdrop-blur-md border-b border-[#D3492D] ${
            mobileOpen ? "max-h-64" : "max-h-0"
          }`}
        >
          <ul className="px-6 py-4 space-y-3">
            {navLinks.map((link) => (
              <li key={link.id}>
                <a
                  href={link.target || "#"}
                  onClick={(e) => handleNavClick(e, link)}
                  data-testid={`mobile-nav-link-${link.id}`}
                  className="block py-2 text-base font-medium text-white hover:text-[#C8D5C0] transition-colors"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </header>

      <CustomOrderDialog open={customOpen} onOpenChange={setCustomOpen} />
    </>
  );
};
